import { useState, useEffect } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

export default function IntegrationConfigurationPage() {
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    const textarea = document.getElementById('requirements') as HTMLTextAreaElement;
    if (textarea) {
      textarea.addEventListener('input', (e) => {
        const target = e.target as HTMLTextAreaElement;
        setCharCount(target.value.length);
      });
    }

    // Radio button styling
    const radioInputs = document.querySelectorAll('input[type="radio"]');
    radioInputs.forEach(input => {
      input.addEventListener('change', () => {
        radioInputs.forEach(radio => {
          const parent = radio.closest('label');
          const checkedIndicator = parent?.querySelector('.checked-radio');
          if (checkedIndicator) {
            if ((radio as HTMLInputElement).checked) {
              checkedIndicator.classList.remove('hidden');
            } else {
              checkedIndicator.classList.add('hidden');
            }
          }
        });
      });
    });
  }, []);

  return (
    <div className="bg-white">
      <style>{`
        .flip-card-container {
          perspective: 1000px;
        }
        
        .flip-card-inner {
          position: relative;
          width: 100%;
          height: 100%;
          transition: transform 0.6s;
          transform-style: preserve-3d;
        }
        
        .flip-card-container:hover .flip-card-inner {
          transform: rotateY(180deg);
        }
        
        .flip-card-front,
        .flip-card-back {
          position: absolute;
          width: 100%;
          height: 100%;
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
        }
        
        .flip-card-back {
          transform: rotateY(180deg);
        }
      `}</style>

      <Header />

      {/* Breadcrumb */}
      <div className="bg-gray-50 py-3 border-b border-gray-100">
        <div className="container mx-auto px-4">
          <div className="flex items-center text-sm text-gray-600">
            <a href="/" className="hover:text-primary">Home</a>
            <div className="w-4 h-4 flex items-center justify-center mx-1">
              <i className="ri-arrow-right-s-line"></i>
            </div>
            <a href="#" className="hover:text-primary">IT Services</a>
            <div className="w-4 h-4 flex items-center justify-center mx-1">
              <i className="ri-arrow-right-s-line"></i>
            </div>
            <span className="text-primary">IT Integration Services</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative w-full" style={{
        backgroundImage: 'url(https://thyamis.com/wp-content/uploads/2024/12/pexels-brett-sayles-2881229-scaled.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}>
        <div className="absolute inset-0 bg-gradient-to-r from-white/90 via-white/70 to-transparent"></div>
        <div className="container mx-auto px-4 py-20 relative">
          <div className="w-full max-w-2xl">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Integration & Configuration Services
            </h1>
            <p className="text-lg text-gray-700 mb-8">
              Seamlessly connect your systems, applications, and data with our comprehensive integration services. We help businesses create unified technology ecosystems that drive efficiency and innovation.
            </p>
            <div className="flex flex-wrap gap-4">
              <button className="bg-primary text-white px-6 py-3 rounded-lg whitespace-nowrap font-medium hover:bg-primary/90 transition-colors cursor-pointer">
                Get Started
              </button>
              <button className="bg-white border border-gray-300 text-gray-800 px-6 py-3 rounded-lg whitespace-nowrap font-medium hover:bg-gray-50 transition-colors cursor-pointer">
                Schedule Consultation
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Integration and Configuration Center Services */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-4 text-gray-900">
            Integration and Configuration Center Services
          </h2>
          <p className="text-gray-600 text-center max-w-3xl mx-auto mb-12">
            Whether you're just starting your integration journey or ready to take your infrastructure to new heights, we offer services that are comprehensive and impactful.
          </p>

          <div className="gap-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 w-full">
            {/* End User Computing */}
            <div className="flip-card-container h-[400px] cursor-pointer">
              <div className="flip-card-inner h-full">
                {/* Front */}
                <div className="flip-card-front">
                  <div className="h-full rounded-lg overflow-hidden shadow-lg">
                    <div className="relative h-full">
                      <img 
                        src="https://public.readdy.ai/ai/img_res/52ade80d488468d9b663d8f890b5260e.jpg" 
                        alt="End User Computing" 
                        className="h-full w-full object-cover object-top" 
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-primary to-primary/80 p-6">
                        <h3 className="text-xl font-semibold text-white">End User Computing (EUC)</h3>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Back */}
                <div className="flip-card-back">
                  <div className="h-full bg-white border-4 border-primary rounded-lg p-8 flex flex-col items-center justify-center text-center shadow-xl">
                    <div className="bg-primary flex items-center justify-center rounded-full w-16 h-16 mb-4">
                      <i className="ri-computer-line text-white text-3xl"></i>
                    </div>
                    <h3 className="text-xl font-semibold mb-4 text-gray-900">End User Computing (EUC)</h3>
                    <p className="text-sm text-gray-600 mb-6">Enhance user experience and productivity with our end-user computing solutions that seamlessly integrate applications and services across devices.</p>
                    <a href="#" className="flex items-center gap-2 text-primary font-medium hover:text-primary/80 cursor-pointer">
                      <span>Learn more</span>
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Device Life Cycle Management */}
            <div className="flip-card-container h-[400px] cursor-pointer">
              <div className="flip-card-inner h-full">
                {/* Front */}
                <div className="flip-card-front">
                  <div className="h-full rounded-lg overflow-hidden shadow-lg">
                    <div className="relative h-full">
                      <img 
                        src="https://readdy.ai/api/search-image?query=IT%20asset%20lifecycle%20management%20with%20devices%20being%20inventoried%20and%20tracked%20in%20modern%20warehouse%20facility%20showing%20procurement%20deployment%20and%20maintenance%20processes%20with%20organized%20equipment&width=400&height=400&seq=dlm002&orientation=squarish" 
                        alt="Device Lifecycle Management" 
                        className="h-full w-full object-cover object-top" 
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-green-600 to-green-500/80 p-6">
                        <h3 className="text-xl font-semibold text-white">Device Life Cycle Management (DLM)</h3>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Back */}
                <div className="flip-card-back">
                  <div className="h-full bg-white border-4 border-green-600 rounded-lg p-8 flex flex-col items-center justify-center text-center shadow-xl">
                    <div className="bg-green-600 flex items-center justify-center rounded-full w-16 h-16 mb-4">
                      <i className="ri-refresh-line text-white text-3xl"></i>
                    </div>
                    <h3 className="text-xl font-semibold mb-4 text-gray-900">Device Life Cycle Management (DLM)</h3>
                    <p className="text-sm text-gray-600 mb-6">Comprehensive management of your device ecosystem from procurement to disposal, ensuring optimal performance and security throughout the lifecycle.</p>
                    <a href="#" className="flex items-center gap-2 text-green-600 font-medium hover:text-green-700 cursor-pointer">
                      <span>Learn more</span>
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Integrated Data Center Service */}
            <div className="flip-card-container h-[400px] cursor-pointer">
              <div className="flip-card-inner h-full">
                {/* Front */}
                <div className="flip-card-front">
                  <div className="h-full rounded-lg overflow-hidden shadow-lg">
                    <div className="relative h-full">
                      <img 
                        src="https://readdy.ai/api/search-image?query=modern%20data%20center%20with%20rows%20of%20server%20racks%20and%20networking%20equipment%20showing%20integrated%20infrastructure%20and%20security%20systems%20with%20blue%20lighting%20and%20organized%20cable%20management&width=400&height=400&seq=idcs002&orientation=squarish" 
                        alt="Integrated Data Center" 
                        className="h-full w-full object-cover object-top" 
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-purple-600 to-purple-500/80 p-6">
                        <h3 className="text-xl font-semibold text-white">Integrated Data Center Service (IDCS)</h3>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Back */}
                <div className="flip-card-back">
                  <div className="h-full bg-white border-4 border-purple-600 rounded-lg p-8 flex flex-col items-center justify-center text-center shadow-xl">
                    <div className="bg-purple-600 flex items-center justify-center rounded-full w-16 h-16 mb-4">
                      <i className="ri-server-line text-white text-3xl"></i>
                    </div>
                    <h3 className="text-xl font-semibold mb-4 text-gray-900">Integrated Data Center Service (IDCS)</h3>
                    <p className="text-sm text-gray-600 mb-6">Optimize your data center operations with our integrated services that connect infrastructure, networking, and security systems for maximum efficiency.</p>
                    <a href="#" className="flex items-center gap-2 text-purple-600 font-medium hover:text-purple-700 cursor-pointer">
                      <span>Learn more</span>
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Hardware Repair Service */}
            <div className="flip-card-container h-[400px] cursor-pointer">
              <div className="flip-card-inner h-full">
                {/* Front */}
                <div className="flip-card-front">
                  <div className="h-full rounded-lg overflow-hidden shadow-lg">
                    <div className="relative h-full">
                      <img 
                        src="https://readdy.ai/api/search-image?query=skilled%20technician%20repairing%20computer%20hardware%20with%20diagnostic%20tools%20and%20equipment%20on%20clean%20workbench%20in%20professional%20repair%20facility%20with%20organized%20workspace%20and%20testing%20instruments&width=400&height=400&seq=hrs002&orientation=squarish" 
                        alt="Hardware Repair Service" 
                        className="h-full w-full object-cover object-top" 
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-orange-600 to-orange-500/80 p-6">
                        <h3 className="text-xl font-semibold text-white">Hardware Repair Service (HRS)</h3>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Back */}
                <div className="flip-card-back">
                  <div className="h-full bg-white border-4 border-orange-600 rounded-lg p-8 flex flex-col items-center justify-center text-center shadow-xl">
                    <div className="bg-orange-600 flex items-center justify-center rounded-full w-16 h-16 mb-4">
                      <i className="ri-tools-line text-white text-3xl"></i>
                    </div>
                    <h3 className="text-xl font-semibold mb-4 text-gray-900">Hardware Repair Service (HRS)</h3>
                    <p className="text-sm text-gray-600 mb-6">Keep your hardware running at peak performance with our comprehensive repair services that integrate with your existing IT management systems.</p>
                    <a href="#" className="flex items-center gap-2 text-orange-600 font-medium hover:text-orange-700 cursor-pointer">
                      <span>Learn more</span>
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* IT Asset Disposition */}
            <div className="flip-card-container h-[400px] cursor-pointer">
              <div className="flip-card-inner h-full">
                {/* Front */}
                <div className="flip-card-front">
                  <div className="h-full rounded-lg overflow-hidden shadow-lg">
                    <div className="relative h-full">
                      <img 
                        src="https://readdy.ai/api/search-image?query=secure%20IT%20asset%20disposal%20and%20data%20destruction%20facility%20with%20certified%20recycling%20processes%20and%20environmental%20compliance%20equipment%20showing%20professional%20handling%20of%20electronic%20waste&width=400&height=400&seq=itad002&orientation=squarish" 
                        alt="IT Asset Disposition" 
                        className="h-full w-full object-cover object-top" 
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-teal-600 to-teal-500/80 p-6">
                        <h3 className="text-xl font-semibold text-white">IT Asset Disposition (ITAD)</h3>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Back */}
                <div className="flip-card-back">
                  <div className="h-full bg-white border-4 border-teal-600 rounded-lg p-8 flex flex-col items-center justify-center text-center shadow-xl">
                    <div className="bg-teal-600 flex items-center justify-center rounded-full w-16 h-16 mb-4">
                      <i className="ri-recycle-line text-white text-3xl"></i>
                    </div>
                    <h3 className="text-xl font-semibold mb-4 text-gray-900">IT Asset Disposition (ITAD)</h3>
                    <p className="text-sm text-gray-600 mb-6">Secure and environmentally responsible disposal of IT assets with comprehensive data destruction and recycling services.</p>
                    <a href="#" className="flex items-center gap-2 text-teal-600 font-medium hover:text-teal-700 cursor-pointer">
                      <span>Learn more</span>
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Refurbishment & Recycling */}
            <div className="flip-card-container h-[400px] cursor-pointer">
              <div className="flip-card-inner h-full">
                {/* Front */}
                <div className="flip-card-front">
                  <div className="h-full rounded-lg overflow-hidden shadow-lg">
                    <div className="relative h-full">
                      <img 
                        src="https://readdy.ai/api/search-image?query=professional%20equipment%20refurbishment%20workshop%20with%20quality%20testing%20stations%20and%20certified%20technicians%20working%20on%20sustainable%20recycling%20programs%20with%20modern%20testing%20equipment%20and%20organized%20workspace&width=400&height=400&seq=refurb002&orientation=squarish" 
                        alt="Refurbishment & Recycling" 
                        className="h-full w-full object-cover object-top" 
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-cyan-600 to-cyan-500/80 p-6">
                        <h3 className="text-xl font-semibold text-white">Refurbishment & Recycling</h3>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Back */}
                <div className="flip-card-back">
                  <div className="h-full bg-white border-4 border-cyan-600 rounded-lg p-8 flex flex-col items-center justify-center text-center shadow-xl">
                    <div className="bg-cyan-600 flex items-center justify-center rounded-full w-16 h-16 mb-4">
                      <i className="ri-refresh-line text-white text-3xl"></i>
                    </div>
                    <h3 className="text-xl font-semibold mb-4 text-gray-900">Refurbishment & Recycling</h3>
                    <p className="text-sm text-gray-600 mb-6">Extend asset lifecycle and reduce environmental impact through professional refurbishment and sustainable recycling programs.</p>
                    <a href="#" className="flex items-center gap-2 text-cyan-600 font-medium hover:text-cyan-700 cursor-pointer">
                      <span>Learn more</span>
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Modals */}
      <div className="bg-white flex flex-col gap-2 items-center justify-center max-w-[calc(100vw-2rem)] md:max-w-[40rem] md:p-4 mfp-hide mx-auto px-2 py-4 relative text-black text-center xl:!hidden" id="euc-modal">
        <div role="presentation" className="bg-primary flex items-center justify-center rounded-full shrink-0 w-16 h-16">
          <i className="ri-computer-line text-white text-3xl"></i>
        </div>
        <div className="flex flex-col gap-3 items-center text-center">
          <h3 className="text-lg font-semibold">End User Computing (EUC)</h3>
          <p className="text-sm text-gray-600">Enhance user experience and productivity with our end-user computing solutions that seamlessly integrate applications and services across devices.</p>
        </div>
        <a href="#" className="flex flex-row gap-1 items-center justify-center text-primary font-medium hover:text-primary/80 cursor-pointer mt-2">
          <span>Learn more</span>
          <i className="ri-arrow-right-line"></i>
        </a>
      </div>

      <div className="bg-white flex flex-col gap-2 items-center justify-center max-w-[calc(100vw-2rem)] md:max-w-[40rem] md:p-4 mfp-hide mx-auto px-2 py-4 relative text-black text-center xl:!hidden" id="dlm-modal">
        <div role="presentation" className="bg-green-500 flex items-center justify-center rounded-full shrink-0 w-16 h-16">
          <i className="ri-refresh-line text-white text-3xl"></i>
        </div>
        <div className="flex flex-col gap-3 items-center text-center">
          <h3 className="text-lg font-semibold">Device Life Cycle Management (DLM)</h3>
          <p className="text-sm text-gray-600">Comprehensive management of your device ecosystem from procurement to disposal, ensuring optimal performance and security throughout the lifecycle.</p>
        </div>
        <a href="#" className="flex flex-row gap-1 items-center justify-center text-green-500 font-medium hover:text-green-600 cursor-pointer mt-2">
          <span>Learn more</span>
          <i className="ri-arrow-right-line"></i>
        </a>
      </div>

      <div className="bg-white flex flex-col gap-2 items-center justify-center max-w-[calc(100vw-2rem)] md:max-w-[40rem] md:p-4 mfp-hide mx-auto px-2 py-4 relative text-black text-center xl:!hidden" id="idcs-modal">
        <div role="presentation" className="bg-purple-500 flex items-center justify-center rounded-full shrink-0 w-16 h-16">
          <i className="ri-server-line text-white text-3xl"></i>
        </div>
        <div className="flex flex-col gap-3 items-center text-center">
          <h3 className="text-lg font-semibold">Integrated Data Center Service (IDCS)</h3>
          <p className="text-sm text-gray-600">Optimize your data center operations with our integrated services that connect infrastructure, networking, and security systems for maximum efficiency.</p>
        </div>
        <a href="#" className="flex flex-row gap-1 items-center justify-center text-purple-500 font-medium hover:text-purple-600 cursor-pointer mt-2">
          <span>Learn more</span>
          <i className="ri-arrow-right-line"></i>
        </a>
      </div>

      <div className="bg-white flex flex-col gap-2 items-center justify-center max-w-[calc(100vw-2rem)] md:max-w-[40rem] md:p-4 mfp-hide mx-auto px-2 py-4 relative text-black text-center xl:!hidden" id="hrs-modal">
        <div role="presentation" className="bg-orange-500 flex items-center justify-center rounded-full shrink-0 w-16 h-16">
          <i className="ri-tools-line text-white text-3xl"></i>
        </div>
        <div className="flex flex-col gap-3 items-center text-center">
          <h3 className="text-lg font-semibold">Hardware Repair Service (HRS)</h3>
          <p className="text-sm text-gray-600">Keep your hardware running at peak performance with our comprehensive repair services that integrate with your existing IT management systems.</p>
        </div>
        <a href="#" className="flex flex-row gap-1 items-center justify-center text-orange-500 font-medium hover:text-orange-600 cursor-pointer mt-2">
          <span>Learn more</span>
          <i className="ri-arrow-right-line"></i>
        </a>
      </div>

      <div className="bg-white flex flex-col gap-2 items-center justify-center max-w-[calc(100vw-2rem)] md:max-w-[40rem] md:p-4 mfp-hide mx-auto px-2 py-4 relative text-black text-center xl:!hidden" id="itad-modal">
        <div role="presentation" className="bg-teal-500 flex items-center justify-center rounded-full shrink-0 w-16 h-16">
          <i className="ri-recycle-line text-white text-3xl"></i>
        </div>
        <div className="flex flex-col gap-3 items-center text-center">
          <h3 className="text-lg font-semibold">IT Asset Disposition (ITAD)</h3>
          <p className="text-sm text-gray-600">Secure and environmentally responsible disposal of IT assets with comprehensive data destruction and recycling services.</p>
        </div>
        <a href="#" className="flex flex-row gap-1 items-center justify-center text-teal-500 font-medium hover:text-teal-600 cursor-pointer mt-2">
          <span>Learn more</span>
          <i className="ri-arrow-right-line"></i>
        </a>
      </div>

      <div className="bg-white flex flex-col gap-2 items-center justify-center max-w-[calc(100vw-2rem)] md:max-w-[40rem] md:p-4 mfp-hide mx-auto px-2 py-4 relative text-black text-center xl:!hidden" id="refurb-modal">
        <div role="presentation" className="bg-cyan-500 flex items-center justify-center rounded-full shrink-0 w-16 h-16">
          <i className="ri-refresh-line text-white text-3xl"></i>
        </div>
        <div className="flex flex-col gap-3 items-center text-center">
          <h3 className="text-lg font-semibold">Refurbishment & Recycling</h3>
          <p className="text-sm text-gray-600">Extend asset lifecycle and reduce environmental impact through professional refurbishment and sustainable recycling programs.</p>
        </div>
        <a href="#" className="flex flex-row gap-1 items-center justify-center text-cyan-500 font-medium hover:text-cyan-600 cursor-pointer mt-2">
          <span>Learn more</span>
          <i className="ri-arrow-right-line"></i>
        </a>
      </div>

      {/* End-User-Computing Services */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-4 text-gray-900">
            End-User-Computing Services
          </h2>
          <p className="text-gray-600 text-center max-w-3xl mx-auto mb-12">
            Delivering the next generation digital workplace that empowers your workforce with seamless technology experiences, ensuring optimal user productivity and satisfaction across all touchpoints.
          </p>

          <div className="gap-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 w-full">
            {/* Device/Image Design */}
            <div className="flip-card-container h-[400px] cursor-pointer">
              <div className="flip-card-inner h-full">
                {/* Front */}
                <div className="flip-card-front">
                  <div className="h-full rounded-lg overflow-hidden shadow-lg">
                    <div className="relative h-full">
                      <img 
                        src="https://readdy.ai/api/search-image?query=professional%20IT%20specialist%20customizing%20computer%20device%20with%20software%20installation%20and%20branding%20configuration%20in%20modern%20tech%20workspace%20with%20multiple%20screens%20showing%20device%20imaging%20and%20deployment%20tools&width=400&height=400&seq=deviceimg003&orientation=squarish" 
                        alt="Device/Image Design" 
                        className="h-full w-full object-cover object-top" 
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-primary to-primary/80 p-6">
                        <h3 className="text-xl font-semibold text-white">Device/Image Design</h3>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Back */}
                <div className="flip-card-back">
                  <div className="h-full bg-white border-4 border-primary rounded-lg p-8 flex flex-col items-center justify-center text-center shadow-xl">
                    <div className="bg-primary flex items-center justify-center rounded-full w-16 h-16 mb-4">
                      <i className="ri-computer-line text-white text-3xl"></i>
                    </div>
                    <h3 className="text-xl font-semibold mb-4 text-gray-900">Device/Image Design</h3>
                    <p className="text-sm text-gray-600 mb-6">Customize devices with tailored software, settings, and branding for a consistent user experience.</p>
                    <a href="#" className="flex items-center gap-2 text-primary font-medium hover:text-primary/80 cursor-pointer">
                      <span>Learn more</span>
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Windows Updates */}
            <div className="flip-card-container h-[400px] cursor-pointer">
              <div className="flip-card-inner h-full">
                {/* Front */}
                <div className="flip-card-front">
                  <div className="h-full rounded-lg overflow-hidden shadow-lg">
                    <div className="relative h-full">
                      <img 
                        src="https://readdy.ai/api/search-image?query=Windows%20operating%20system%20update%20installation%20screen%20showing%20security%20patches%20and%20system%20updates%20being%20applied%20on%20modern%20computer%20monitor%20in%20professional%20IT%20environment%20with%20progress%20indicators%20and%20update%20management%20interface&width=400&height=400&seq=winupdate003&orientation=squarish" 
                        alt="Windows Updates" 
                        className="h-full w-full object-cover object-top" 
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-purple-600 to-purple-500/80 p-6">
                        <h3 className="text-xl font-semibold text-white">Windows Updates</h3>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Back */}
                <div className="flip-card-back">
                  <div className="h-full bg-white border-4 border-purple-600 rounded-lg p-8 flex flex-col items-center justify-center text-center shadow-xl">
                    <div className="bg-purple-600 flex items-center justify-center rounded-full w-16 h-16 mb-4">
                      <i className="ri-windows-line text-white text-3xl"></i>
                    </div>
                    <h3 className="text-xl font-semibold mb-4 text-gray-900">Windows Updates</h3>
                    <p className="text-sm text-gray-600 mb-6">Pre-install the latest security patches and updates to minimize downtime and security risks.</p>
                    <a href="#" className="flex items-center gap-2 text-purple-600 font-medium hover:text-purple-700 cursor-pointer">
                      <span>Learn more</span>
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Autopilot Deployment */}
            <div className="flip-card-container h-[400px] cursor-pointer">
              <div className="flip-card-inner h-full">
                {/* Front */}
                <div className="flip-card-front">
                  <div className="h-full rounded-lg overflow-hidden shadow-lg">
                    <div className="relative h-full">
                      <img 
                        src="https://readdy.ai/api/search-image?query=automated%20device%20deployment%20system%20with%20zero-touch%20provisioning%20technology%20showing%20multiple%20devices%20being%20configured%20simultaneously%20in%20modern%20IT%20operations%20center%20with%20automation%20dashboard%20and%20cloud%20management%20interface&width=400&height=400&seq=autopilot003&orientation=squarish" 
                        alt="Autopilot Deployment" 
                        className="h-full w-full object-cover object-top" 
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-teal-600 to-teal-500/80 p-6">
                        <h3 className="text-xl font-semibold text-white">Autopilot Deployment</h3>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Back */}
                <div className="flip-card-back">
                  <div className="h-full bg-white border-4 border-teal-600 rounded-lg p-8 flex flex-col items-center justify-center text-center shadow-xl">
                    <div className="bg-teal-600 flex items-center justify-center rounded-full w-16 h-16 mb-4">
                      <i className="ri-steering-2-line text-white text-3xl"></i>
                    </div>
                    <h3 className="text-xl font-semibold mb-4 text-gray-900">Autopilot Deployment</h3>
                    <p className="text-sm text-gray-600 mb-6">Automate new device setup with zero-touch provisioning for a secure, consistent experience.</p>
                    <a href="#" className="flex items-center gap-2 text-teal-600 font-medium hover:text-teal-700 cursor-pointer">
                      <span>Learn more</span>
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Asset Tagging/Etching */}
            <div className="flip-card-container h-[400px] cursor-pointer">
              <div className="flip-card-inner h-full">
                {/* Front */}
                <div className="flip-card-front">
                  <div className="h-full rounded-lg overflow-hidden shadow-lg">
                    <div className="relative h-full">
                      <img 
                        src="https://readdy.ai/api/search-image?query=IT%20asset%20tagging%20process%20with%20barcode%20scanner%20and%20RFID%20technology%20tracking%20computer%20equipment%20in%20organized%20inventory%20management%20facility%20with%20permanent%20identification%20labels%20and%20real-time%20monitoring%20system&width=400&height=400&seq=assettag003&orientation=squarish" 
                        alt="Asset Tagging/Etching" 
                        className="h-full w-full object-cover object-top" 
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-amber-600 to-amber-500/80 p-6">
                        <h3 className="text-xl font-semibold text-white">Asset Tagging/Etching</h3>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Back */}
                <div className="flip-card-back">
                  <div className="h-full bg-white border-4 border-amber-600 rounded-lg p-8 flex flex-col items-center justify-center text-center shadow-xl">
                    <div className="bg-amber-600 flex items-center justify-center rounded-full w-16 h-16 mb-4">
                      <i className="ri-barcode-line text-white text-3xl"></i>
                    </div>
                    <h3 className="text-xl font-semibold mb-4 text-gray-900">Asset Tagging/Etching</h3>
                    <p className="text-sm text-gray-600 mb-6">Track devices with permanent IDs and real-time monitoring to prevent loss and theft.</p>
                    <a href="#" className="flex items-center gap-2 text-amber-600 font-medium hover:text-amber-700 cursor-pointer">
                      <span>Learn more</span>
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Encryption */}
            <div className="flip-card-container h-[400px] cursor-pointer">
              <div className="flip-card-inner h-full">
                {/* Front */}
                <div className="flip-card-front">
                  <div className="h-full rounded-lg overflow-hidden shadow-lg">
                    <div className="relative h-full">
                      <img 
                        src="https://readdy.ai/api/search-image?query=enterprise%20data%20encryption%20and%20security%20system%20with%20digital%20lock%20symbols%20and%20encrypted%20data%20streams%20on%20computer%20screens%20in%20secure%20IT%20facility%20showing%20cybersecurity%20protection%20and%20compliance%20standards&width=400&height=400&seq=encrypt003&orientation=squarish" 
                        alt="Encryption" 
                        className="h-full w-full object-cover object-top" 
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-rose-600 to-rose-500/80 p-6">
                        <h3 className="text-xl font-semibold text-white">Encryption</h3>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Back */}
                <div className="flip-card-back">
                  <div className="h-full bg-white border-4 border-rose-600 rounded-lg p-8 flex flex-col items-center justify-center text-center shadow-xl">
                    <div className="bg-rose-600 flex items-center justify-center rounded-full w-16 h-16 mb-4">
                      <i className="ri-lock-password-line text-white text-3xl"></i>
                    </div>
                    <h3 className="text-xl font-semibold mb-4 text-gray-900">Encryption</h3>
                    <p className="text-sm text-gray-600 mb-6">Secure data and meet compliance with enterprise-grade encryption across all devices.</p>
                    <a href="#" className="flex items-center gap-2 text-rose-600 font-medium hover:text-rose-700 cursor-pointer">
                      <span>Learn more</span>
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* System Configuration */}
            <div className="flip-card-container h-[400px] cursor-pointer">
              <div className="flip-card-inner h-full">
                {/* Front */}
                <div className="flip-card-front">
                  <div className="h-full rounded-lg overflow-hidden shadow-lg">
                    <div className="relative h-full">
                      <img 
                        src="https://readdy.ai/api/search-image?query=IT%20system%20configuration%20and%20settings%20management%20with%20technical%20specialist%20adjusting%20computer%20parameters%20and%20security%20policies%20on%20multiple%20monitors%20in%20professional%20technology%20workspace%20with%20configuration%20tools&width=400&height=400&seq=sysconfig003&orientation=squarish" 
                        alt="System Configuration" 
                        className="h-full w-full object-cover object-top" 
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-indigo-600 to-indigo-500/80 p-6">
                        <h3 className="text-xl font-semibold text-white">System Configuration</h3>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Back */}
                <div className="flip-card-back">
                  <div className="h-full bg-white border-4 border-indigo-600 rounded-lg p-8 flex flex-col items-center justify-center text-center shadow-xl">
                    <div className="bg-indigo-600 flex items-center justify-center rounded-full w-16 h-16 mb-4">
                      <i className="ri-settings-3-line text-white text-3xl"></i>
                    </div>
                    <h3 className="text-xl font-semibold mb-4 text-gray-900">System Configuration</h3>
                    <p className="text-sm text-gray-600 mb-6">Ensure consistent user experiences and meet security standards with expertly configured devices.</p>
                    <a href="#" className="flex items-center gap-2 text-indigo-600 font-medium hover:text-indigo-700 cursor-pointer">
                      <span>Learn more</span>
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* MDM Configuration */}
            <div className="flip-card-container h-[400px] cursor-pointer">
              <div className="flip-card-inner h-full">
                {/* Front */}
                <div className="flip-card-front">
                  <div className="h-full rounded-lg overflow-hidden shadow-lg">
                    <div className="relative h-full">
                      <img 
                        src="https://readdy.ai/api/search-image?query=mobile%20device%20management%20dashboard%20with%20centralized%20control%20interface%20showing%20smartphones%20and%20tablets%20being%20managed%20remotely%20with%20security%20policies%20and%20app%20deployment%20in%20modern%20IT%20operations%20center&width=400&height=400&seq=mdmconfig003&orientation=squarish" 
                        alt="MDM Configuration" 
                        className="h-full w-full object-cover object-top" 
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-emerald-600 to-emerald-500/80 p-6">
                        <h3 className="text-xl font-semibold text-white">MDM Configuration</h3>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Back */}
                <div className="flip-card-back">
                  <div className="h-full bg-white border-4 border-emerald-600 rounded-lg p-8 flex flex-col items-center justify-center text-center shadow-xl">
                    <div className="bg-emerald-600 flex items-center justify-center rounded-full w-16 h-16 mb-4">
                      <i className="ri-smartphone-line text-white text-3xl"></i>
                    </div>
                    <h3 className="text-xl font-semibold mb-4 text-gray-900">MDM Configuration</h3>
                    <p className="text-sm text-gray-600 mb-6">Manage and secure mobile devices centrally with seamless MDM integration.</p>
                    <a href="#" className="flex items-center gap-2 text-emerald-600 font-medium hover:text-emerald-700 cursor-pointer">
                      <span>Learn more</span>
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Kitting/Packaging */}
            <div className="flip-card-container h-[400px] cursor-pointer">
              <div className="flip-card-inner h-full">
                {/* Front */}
                <div className="flip-card-front">
                  <div className="h-full rounded-lg overflow-hidden shadow-lg">
                    <div className="relative h-full">
                      <img 
                        src="https://readdy.ai/api/search-image?query=professional%20device%20kitting%20and%20packaging%20operation%20with%20complete%20computer%20packages%20being%20assembled%20with%20accessories%20cables%20and%20documentation%20in%20organized%20fulfillment%20center%20ready%20for%20rapid%20deployment&width=400&height=400&seq=kitting003&orientation=squarish" 
                        alt="Kitting/Packaging" 
                        className="h-full w-full object-cover object-top" 
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-cyan-600 to-cyan-500/80 p-6">
                        <h3 className="text-xl font-semibold text-white">Kitting/Packaging</h3>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Back */}
                <div className="flip-card-back">
                  <div className="h-full bg-white border-4 border-cyan-600 rounded-lg p-8 flex flex-col items-center justify-center text-center shadow-xl">
                    <div className="bg-cyan-600 flex items-center justify-center rounded-full w-16 h-16 mb-4">
                      <i className="ri-box-3-line text-white text-3xl"></i>
                    </div>
                    <h3 className="text-xl font-semibold mb-4 text-gray-900">Kitting/Packaging</h3>
                    <p className="text-sm text-gray-600 mb-6">Provide complete device packages for rapid deployment and immediate productivity.</p>
                    <a href="#" className="flex items-center gap-2 text-cyan-600 font-medium hover:text-cyan-700 cursor-pointer">
                      <span>Learn more</span>
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Cellular Activation */}
            <div className="flip-card-container h-[400px] cursor-pointer">
              <div className="flip-card-inner h-full">
                {/* Front */}
                <div className="flip-card-front">
                  <div className="h-full rounded-lg overflow-hidden shadow-lg">
                    <div className="relative h-full">
                      <img 
                        src="https://readdy.ai/api/search-image?query=cellular%20network%20activation%20with%20mobile%20devices%20showing%20signal%20connectivity%20and%20wireless%20communication%20setup%20in%20modern%20telecommunications%20facility%20with%20network%20testing%20equipment%20and%20activation%20systems&width=400&height=400&seq=cellular003&orientation=squarish" 
                        alt="Cellular Activation" 
                        className="h-full w-full object-cover object-top" 
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-fuchsia-600 to-fuchsia-500/80 p-6">
                        <h3 className="text-xl font-semibold text-white">Cellular Activation</h3>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Back */}
                <div className="flip-card-back">
                  <div className="h-full bg-white border-4 border-fuchsia-600 rounded-lg p-8 flex flex-col items-center justify-center text-center shadow-xl">
                    <div className="bg-fuchsia-600 flex items-center justify-center rounded-full w-16 h-16 mb-4">
                      <i className="ri-signal-tower-line text-white text-3xl"></i>
                    </div>
                    <h3 className="text-xl font-semibold mb-4 text-gray-900">Cellular Activation</h3>
                    <p className="text-sm text-gray-600 mb-6">Activate connected devices out of the box to eliminate setup delays.</p>
                    <a href="#" className="flex items-center gap-2 text-fuchsia-600 font-medium hover:text-fuchsia-700 cursor-pointer">
                      <span>Learn more</span>
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Lifecycle Management */}
            <div className="flip-card-container h-[400px] cursor-pointer">
              <div className="flip-card-inner h-full">
                {/* Front */}
                <div className="flip-card-front">
                  <div className="h-full rounded-lg overflow-hidden shadow-lg">
                    <div className="relative h-full">
                      <img 
                        src="https://readdy.ai/api/search-image?query=complete%20IT%20device%20lifecycle%20management%20showing%20procurement%20deployment%20maintenance%20and%20disposal%20stages%20with%20circular%20workflow%20diagram%20in%20professional%20technology%20management%20facility%20with%20asset%20tracking%20systems&width=400&height=400&seq=lifecycle003&orientation=squarish" 
                        alt="Lifecycle Management" 
                        className="h-full w-full object-cover object-top" 
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-lime-600 to-lime-500/80 p-6">
                        <h3 className="text-xl font-semibold text-white">Lifecycle Management</h3>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Back */}
                <div className="flip-card-back">
                  <div className="h-full bg-white border-4 border-lime-600 rounded-lg p-8 flex flex-col items-center justify-center text-center shadow-xl">
                    <div className="bg-lime-600 flex items-center justify-center rounded-full w-16 h-16 mb-4">
                      <i className="ri-refresh-line text-white text-3xl"></i>
                    </div>
                    <h3 className="text-xl font-semibold mb-4 text-gray-900">Lifecycle Management</h3>
                    <p className="text-sm text-gray-600 mb-6">Manage devices from procurement to disposal, reducing total cost of ownership.</p>
                    <a href="#" className="flex items-center gap-2 text-lime-600 font-medium hover:text-lime-700 cursor-pointer">
                      <span>Learn more</span>
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Onboarding Approach */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-4">Our Customer Onboarding Approach</h2>
          <p className="text-gray-600 text-center max-w-3xl mx-auto mb-12">
            We follow a proven methodology to ensure successful delivery of Integration services for the best user experience
          </p>

          <div className="relative">
            <div className="hidden md:block absolute top-1/2 left-0 right-0 h-1 bg-gray-200 -translate-y-1/2 z-0"></div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative z-10">
              {/* Pre-Sales */}
              <div className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-50 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <div className="w-8 h-8 flex items-center justify-center text-primary">
                    <i className="ri-brain-line ri-2x"></i>
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-4 text-center">Pre-Sales</h3>
                <p className="text-gray-600 mb-4">
                  Understand client needs, scope the solution, and build trust.
                </p>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                      <i className="ri-checkbox-circle-line"></i>
                    </div>
                    <span>Customer Requirements</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                      <i className="ri-checkbox-circle-line"></i>
                    </div>
                    <span>Solution Design</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                      <i className="ri-checkbox-circle-line"></i>
                    </div>
                    <span>Proposal & SOW</span>
                  </li>
                </ul>
              </div>

              {/* Onboarding & Planning */}
              <div className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100">
                <div className="w-16 h-16 bg-gradient-to-br from-green-50 to-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <div className="w-8 h-8 flex items-center justify-center text-green-600">
                    <i className="ri-tools-line ri-2x"></i>
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-4 text-center">Onboarding & Planning</h3>
                <p className="text-gray-600 mb-4">
                  Prepare for execution with detailed planning and stakeholder alignment.
                </p>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-green-600 mt-0.5 mr-2">
                      <i className="ri-checkbox-circle-line"></i>
                    </div>
                    <span>Project Kickoff</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-green-600 mt-0.5 mr-2">
                      <i className="ri-checkbox-circle-line"></i>
                    </div>
                    <span>Resource Allocation</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-green-600 mt-0.5 mr-2">
                      <i className="ri-checkbox-circle-line"></i>
                    </div>
                    <span>Timeline Planning</span>
                  </li>
                </ul>
              </div>

              {/* Build & Configuration */}
              <div className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-50 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <div className="w-8 h-8 flex items-center justify-center text-purple-600">
                    <i className="ri-settings-3-line ri-2x"></i>
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-4 text-center">Build & Configuration</h3>
                <p className="text-gray-600 mb-4">
                  Prepare devices and kits for deployment.
                </p>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-purple-600 mt-0.5 mr-2">
                      <i className="ri-checkbox-circle-line"></i>
                    </div>
                    <span>Device Setup</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-purple-600 mt-0.5 mr-2">
                      <i className="ri-checkbox-circle-line"></i>
                    </div>
                    <span>Software Installation</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-purple-600 mt-0.5 mr-2">
                      <i className="ri-checkbox-circle-line"></i>
                    </div>
                    <span>First Article Inspection</span>
                  </li>
                </ul>
              </div>

              {/* Delivery & Support */}
              <div className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100">
                <div className="w-16 h-16 bg-gradient-to-br from-amber-50 to-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <div className="w-8 h-8 flex items-center justify-center text-amber-600">
                    <i className="ri-box-3-line ri-2x"></i>
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-4 text-center">Delivery & Support</h3>
                <p className="text-gray-600 mb-4">
                  Deliver ready-to-use devices and ensure smooth user experience.
                </p>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-amber-600 mt-0.5 mr-2">
                      <i className="ri-checkbox-circle-line"></i>
                    </div>
                    <span>Logistics Management</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-amber-600 mt-0.5 mr-2">
                      <i className="ri-checkbox-circle-line"></i>
                    </div>
                    <span>User Training</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 flex items-center justify-center text-amber-600 mt-0.5 mr-2">
                      <i className="ri-checkbox-circle-line"></i>
                    </div>
                    <span>Support Handover</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-4">Start Your Integration Project</h2>
            <p className="text-gray-600 text-center max-w-3xl mx-auto mb-12">
              Contact our Business Development Managers today to discuss how we can help deliver the best End User Experience to your employees.
            </p>

            <div className="flex flex-col lg:flex-row gap-8">
              {/* Form */}
              <div className="w-full lg:w-2/3 bg-white p-8 rounded-lg shadow-sm border border-gray-100">
                <form data-readdy-form id="integration-inquiry-form" action="https://readdy.ai/api/form/d387dg98vlfrnhb0d500" method="post">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="name" className="block text-gray-700 font-medium mb-2">
                        Full Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="Your name"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="company" className="block text-gray-700 font-medium mb-2">
                        Company
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="Your company"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-gray-700 font-medium mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="your.email@company.com"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-gray-700 font-medium mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="(123) 456-7890"
                        required
                      />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label htmlFor="requirements" className="block text-gray-700 font-medium mb-2">
                      Project Requirements
                    </label>
                    <textarea
                      id="requirements"
                      name="requirements"
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Please describe your integration needs and requirements"
                      required
                    ></textarea>
                    <div className="text-sm text-gray-500 mt-1">
                      <span id="char-count">{charCount}</span>/500 characters
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-gray-700 font-medium mb-2">
                      Preferred Contact Method
                    </label>
                    <div className="flex space-x-4">
                      <label className="flex items-center cursor-pointer">
                        <div className="relative w-5 h-5 mr-2">
                          <input
                            type="radio"
                            name="contact_method"
                            value="email"
                            className="opacity-0 absolute w-full h-full cursor-pointer z-10"
                            required
                          />
                          <div className="w-5 h-5 border border-gray-300 rounded-full"></div>
                          <div className="absolute inset-0 hidden checked-radio">
                            <div className="absolute inset-1 bg-primary rounded-full"></div>
                          </div>
                        </div>
                        <span>Email</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <div className="relative w-5 h-5 mr-2">
                          <input
                            type="radio"
                            name="contact_method"
                            value="phone"
                            className="opacity-0 absolute w-full h-full cursor-pointer z-10"
                            required
                          />
                          <div className="w-5 h-5 border border-gray-300 rounded-full"></div>
                          <div className="absolute inset-0 hidden checked-radio">
                            <div className="absolute inset-1 bg-primary rounded-full"></div>
                          </div>
                        </div>
                        <span>Phone</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <div className="relative w-5 h-5 mr-2">
                          <input
                            type="radio"
                            name="contact_method"
                            value="meeting"
                            className="opacity-0 absolute w-full h-full cursor-pointer z-10"
                            required
                          />
                          <div className="w-5 h-5 border border-gray-300 rounded-full"></div>
                          <div className="absolute inset-0 hidden checked-radio">
                            <div className="absolute inset-1 bg-primary rounded-full"></div>
                          </div>
                        </div>
                        <span>Video Meeting</span>
                      </label>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="bg-primary text-white px-6 py-3 rounded-lg whitespace-nowrap font-medium hover:bg-primary/90 transition-colors cursor-pointer"
                  >
                    Submit Inquiry
                  </button>
                </form>
              </div>

              {/* Alternative Contact Methods */}
              <div className="w-full lg:w-1/3 bg-gray-50 p-8 rounded-lg">
                <h3 className="text-xl font-semibold mb-4">Alternative Contact Methods</h3>
                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center mr-4">
                      <div className="w-5 h-5 flex items-center justify-center text-primary">
                        <i className="ri-phone-line"></i>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold">Call Us</h4>
                      <p className="text-gray-600">+31 (0)6 1074 9063</p>
                      <p className="text-gray-600">Monday-Friday, 8am-6pm EST</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center mr-4">
                      <div className="w-5 h-5 flex items-center justify-center text-primary">
                        <i className="ri-mail-line"></i>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold">Email Us</h4>
                      <p className="text-gray-600">sales@thyamis.com</p>
                      <p className="text-gray-600">We respond within 24 hours</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center mr-4">
                      <div className="w-5 h-5 flex items-center justify-center text-primary">
                        <i className="ri-calendar-line"></i>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold">Schedule a Meeting</h4>
                      <p className="text-gray-600">
                        Book a 30-minute consultation with our integration specialists
                      </p>
                      <a
                        href="#"
                        className="text-primary font-medium mt-1 inline-block hover:text-primary/80 cursor-pointer"
                      >
                        View Calendar
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
